import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { GridComponent, PageChangeEvent, PageSizeItem, RowArgs, RowClassArgs, SelectableSettings, SortSettings
} from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { Observable, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { datePipeLocale } from 'src/app/shared/constants';
import { AccessInfo } from 'src/app/shared/shared-access-module/openapi';
import { AccessControlService } from 'src/app/shared/shared-access-module/services/access-control.service';
import {
  ApiFilterRequest,
  GridSortDescriptor,
  IntegraSearchConfigColumn,
  IntegraSearchConfigRow, IntegraSearchOperators, SearchDataList
} from 'src/app/shared/shared-interfaces';
import { BreadcrumbsService } from 'src/app/shared/shared-services/breadcrumbs/breadcrumbs.service';
import { JobGridList } from '../../interfaces/job-grid-list';
import { SchedulerBreadcrumbsService } from '../../services/scheduler-breadcrumbs.service';
import { SchedulerService } from '../../services/scheduler.service';
import { ActivatedRoute, Router } from '@angular/router';
import { removeWildCard } from '../../../../shared/utils/filter.utils';
import { SchedulerNotificationService } from '../../services/scheduler.notification.service';

@Component({
  selector: 'prism-jobs',
  templateUrl: './jobs-list.component.html',
  styleUrls: ['./jobs-list.component.scss']
})
export class JobsComponent implements OnInit, OnDestroy {
  @ViewChild('jobListGrid') jobListGrid: GridComponent;
  public breadcrumbs: string;
  public height: number;
  public gridPadding = 275;
  public selectedIdxs: Array<number> = [];
  public pageSizes: PageSizeItem[] = [
    { text:  '25', value:  25 },
    { text:  '50', value:  50 },
    { text: '100', value: 100 }
  ];
  public sort: GridSortDescriptor[] = [];
  public sortDescriptor: Array<SortDescriptor> = [];
  public datePipeLocale = datePipeLocale;

  public sortSettings: SortSettings = {
    mode: 'single',
    initialDirection: 'desc'
  };

  public selectableSettings: SelectableSettings = {
    checkboxOnly: true
  };

  public skip: number;
  public take = 25;

  public isLoading = false;
  public jobsList: Array<JobGridList> = [];
  private completeJobsList: Array<string> = [];

  private unsubscribe$: Subject<void> = new Subject<void>();
  public totalRows: number;
  public currentDetailExpanded: string;

  public advSearchConfig: IntegraSearchConfigRow[];

  private viewAll = false;
  private filter: ApiFilterRequest[] = [];

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private breadCrumbService: BreadcrumbsService,
    private schedulerBreadCrumbsService: SchedulerBreadcrumbsService,
    private elementRef: ElementRef,
    private schedulerService: SchedulerService,
    private acmControlService: AccessControlService,
    private readonly schedulerNotification: SchedulerNotificationService,
  ) {}

  ngOnInit(): void {
    this.breadcrumbs = 'Home / Scheduled Jobs';
    this.breadCrumbService.setItems(this.schedulerBreadCrumbsService.getItems());
    this.loadAdvSearchConfig();
    this.getDefaultGridHeight();
    this.resetJobListGridState();
    this.initializeJobs();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.breadCrumbService.reset();
  }

  public hasParams(dataItem: JobGridList, paramType: 'input' | 'output'): boolean {
    if (paramType === 'input') {
      return dataItem.jobDtls?.inputParams?.length > 0;
    }

    return dataItem.jobDtls?.outputParams?.length > 0;
  }

  public getRowStyle(context: RowClassArgs): {[key: string]: boolean} {
    const isOdd = (context.index % 2 === 0);
    return {
      even: !isOdd,
      odd: !!isOdd
    };
  }

  public handleDetailExpand(event: {dataItem: JobGridList}): void {
    this.isLoading = true;
    const job = this.jobsList.find(curJob => curJob.jobId === event.dataItem.jobId);
    this.schedulerService.getJobDetailsById(event.dataItem.jobId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(details => {
        job.jobDtls = details;
      }).add(() => this.isLoading = false);
    this.currentDetailExpanded = event.dataItem.jobId;
  }

  public handleDetailCollapse(event: {dataItem: JobGridList}): void {
    if (this.currentDetailExpanded === event.dataItem.jobId) {
      this.currentDetailExpanded = null;
    }
  }

  public handlePageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.take = event.take;
    this.loadJobs();
  }

  public handleFilterEvent(filters: ApiFilterRequest[]): void {
    this.updateQueryParams(filters);
    this.viewAll = filters.findIndex(x => x.property === 'viewAll') >= 0;
    this.filter = this.viewAll ? filters.slice(0, -1) : filters;
    this.loadJobs(true);
  }

  private loadJobs(reset = false): void {
    if (reset) {
      this.resetJobListGridState();
    }
    this.isLoading = true;
    this.schedulerService.getJobs(this.take, this.skip, this.filter, this.viewAll)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(result => {
        this.jobsList = result.items;

        this.jobsList.forEach(job => {
          if (this.completeJobsList.indexOf(job.jobId) < 0) {
            this.completeJobsList.push(job.jobId);
          }
        });

        if (result.restartRowNum > '') {
          this.totalRows = this.completeJobsList.length + this.take;
        } else {
          this.totalRows = this.completeJobsList.length;
        }
      }).add(() => {
        this.isLoading = false;
        this.currentDetailExpanded = null;
      });
  }

  public isRowSelected = (row: RowArgs) => this.selectedIdxs.indexOf(row.index) >= 0;

  public isDetailExpanded(event: { dataItem: JobGridList}): boolean {
    return this.currentDetailExpanded === event.dataItem.jobId;
  }

  private getDefaultGridHeight(): void {
    const layout = this.elementRef.nativeElement.closest('.layout-container');
    if (layout) {
      this.height = (layout.clientHeight - this.gridPadding);
    }
  }

  private resetJobListGridState(): void {
    this.skip = 0;
    this.totalRows = 0;
    this.take = 25;
    this.jobsList = [];
  }

  private loadAdvSearchConfig(): void {
    const showViewAllCheckbox = this.acmControlService.getAccessForWidget('view.all.report', 'sched')
      .includes(AccessInfo.ActionsEnum.Visible);
    this.advSearchConfig = [
      {
        columns: [{
          type: 'string',
          label: 'Search by Job ID, Job Description (min 3 characters)',
          placeholder: 'Job ID, Job Description',
          value: this.getQueryParam('wildcard'),
          key: 'WildSearch',
          property: 'wildcard',
          minLength: 3
        }]
      },
      {
        columns: [
          {
            type: 'combobox',
            label: 'Status',
            value: this.getQueryParam('execStatus'),
            key: 'Status',
            property: 'execStatus',
            placeholder: 'Select Status',
            operator: IntegraSearchOperators.equals,
            data: [
              { id: 'pending', displayName: 'Pending', displaySelected: 'Pending' },
              { id: 'running', displayName: 'Running', displaySelected: 'Running' },
              { id: 'success', displayName: 'Success', displaySelected: 'Success' },
              { id: 'failed', displayName: 'Failed', displaySelected: 'Failed'}
            ],
            filterable: false,
            fetchSingleItem: this.resolveStatus.bind(this)
          },
          {
            type: 'combobox',
            label: 'Job Type',
            value: this.getQueryParam('taskType'),
            key: 'JobType',
            property: 'taskType',
            placeholder: 'Select Action Type',
            filterable: false,
            operator: IntegraSearchOperators.equals,
            data: [
              { id: 'report', displayName: 'Report', displaySelected: 'Report' },
              { id: 'batch', displayName: 'Batch', displaySelected: 'Batch' }
            ],
            fetchSingleItem: this.resolveTaskType.bind(this)
          }
        ]
      }
    ];
    if (showViewAllCheckbox) {
      this.advSearchConfig.push({
        columns: [{
          type: 'checkbox',
          label: '',
          value: this.getQueryParam('viewAll'),
          key: 'ViewAll',
          property: 'viewAll',
          options: [
            { key: 'true', label: 'View All Jobs'}
          ]
        }]
      });
    }
  }

  private getAdvancedSearchColumnByKey(key: string): IntegraSearchConfigColumn {
    let matchingControl: IntegraSearchConfigColumn = null;
    this.advSearchConfig.forEach(row => {
      row.columns.forEach(control => {
        if (control.key === key) {
          matchingControl = control;
        }
      });
    });
    return matchingControl;
  }

  private getAdvancedSearchColumnByProp(property: string, operator: string): IntegraSearchConfigColumn {
    this.advSearchConfig.forEach(row => {
      row.columns.forEach(control => {
        if (control.property === property && control.operator === operator) {
          return control;
        }
      });
    });
    return null;
  }

  private updateQueryParams(searchFilters: Array<ApiFilterRequest>): void {
    const queryParams = searchFilters.reduce((reduced, filter) => {
      const config = this.getAdvancedSearchColumnByProp(filter.property, filter.operator);
      reduced[config ? config.key : filter.property] = removeWildCard(filter.value ?? '');
      return reduced;
    }, {});

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams
    });
  }

  private getQueryParam(property: string): string {
    if (this.activatedRoute.snapshot.queryParamMap.has(property)) {
      return this.activatedRoute.snapshot.queryParamMap.get(property);
    }
    return null;
  }

  private initializeJobs(): void {
    // only load jobs if no query parameters are found
    if (this.activatedRoute.snapshot.queryParamMap.keys.length === 0) {
      this.loadJobs();
    }
    // subscribe to jobs channel
    this.schedulerNotification.subscribeToJobs()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.loadJobs();
    });
  }

  private resolveStatus(status: any): Observable<SearchDataList> {
    const statusData = this.getAdvancedSearchColumnByKey('Status').data
      .find(datum => datum.id === status);
    if (statusData) {
      return of(statusData);
    }
    return of();
  }

  private resolveTaskType(taskType: any): Observable<SearchDataList> {
    const transFilterData = this.getAdvancedSearchColumnByKey('JobType').data
      .find(datum => datum.id === taskType);
    if (transFilterData) {
      return of(transFilterData);
    }
    return of();
  }
}
